import { useState } from 'react'

import { getDay } from 'date-fns'
import _ from 'lodash'
import { styled } from 'styled-components'

import Icon from 'core/components/Icon'
import Button from 'core/components/lib/Button'
import Spinner from 'core/components/lib/Spinner'
import { R, getNow } from 'core/helpers'
import { useOpenHoursWithFallback } from 'core/hooks'
import { variables } from 'core/styles'
import { SingleDayHours, SingleHoliday, Hours } from 'core/types/lenderConfig'

import formatDayOfWeekHours from './helpers/formatDayOfWeekHours'

type SingleDayHoursProps = {
  isBold?: boolean
}

type DayOfWeekHoursListProps = {
  expanded?: boolean
}

const HoursSection = styled.div`
  display: flex;
  align-items: flex-start;
  color: ${variables.colorBlack70};
`
const SingleDay = styled.div<SingleDayHoursProps>`
  display: flex;
  margin-bottom: 8px;
  font-weight: ${(p) => (p.isBold ? '500' : 'normal')};
`
const Day = styled.div`
  margin-right: 16px;
  min-width: 120px;
  white-space: nowrap;
`

const sortedDaysOfWeek = (dayOfWeek: number, hours?: Array<SingleDayHours>) => {
  // Shouldn't be falsy but placed here just in case,
  // and also to prevent TS error
  if (!hours || _.isEmpty(hours)) return null

  // Early return if dayOfWeek is Monday
  if (dayOfWeek === 0) return hours

  const arrayStart = hours.slice(dayOfWeek)
  const arrayEnd = hours.slice(0, dayOfWeek)
  return [...arrayStart, ...arrayEnd]
}

const DayOfWeekHoursList = ({ expanded = true }: DayOfWeekHoursListProps) => {
  const [showHours, setShowHours] = useState(expanded)
  const [showHolidays, setShowHolidays] = useState(expanded)

  const { openHours, isLoading } = useOpenHoursWithFallback()

  const businessHours = openHours?.businessHours as Hours | undefined
  const { holidays, normal } = businessHours ?? {}
  const holidaysByDate = R.indexBy(holidays ?? [], R.prop('date'))

  const normalIsString = typeof normal === 'string'

  // API string format (e.g. "Mon-Fri 9:00-17:00; Sat 10:00-14:00; Sun Closed")
  if (normalIsString && normal) {
    const holidayList: Array<SingleHoliday> = holidays ?? []
    return isLoading ?
        <Spinner />
      : <div>
          <HoursSection>
            <div>
              {normal.split(';').map((part, index) =>
                !showHours && index > 0 ?
                  null
                : <SingleDay isBold={index === 0} key={index}>
                    <Day>{index === 0 ? 'Hours' : ''}</Day>
                    <div>{part.trim()}</div>
                  </SingleDay>,
              )}
            </div>
            {showHours ?
              <Button data-testid='button-arrow-up-hours' fontSize='24px' onClick={() => setShowHours(false)}>
                <Icon name='arrow_drop_up' />
              </Button>
            : <Button data-testid='button-arrow-down-hours' fontSize='24px' onClick={() => setShowHours(true)}>
                <Icon name='arrow_drop_down' />
              </Button>
            }
          </HoursSection>
          {holidayList.length > 0 && (
            <HoursSection>
              <div>
                {holidayList.map((h, index) =>
                  !showHolidays && index > 0 ?
                    null
                  : <SingleDay isBold={index === 0} key={h.date ?? index}>
                      <Day>{index === 0 ? 'Holidays' : ''}</Day>
                      <div>
                        {h.name ?? h.date ?? '—'} {h.hours === '24hours' ? 'Open 24 hours' : h.hours || 'Closed'}
                      </div>
                    </SingleDay>,
                )}
              </div>
              {showHolidays ?
                <Button data-testid='button-arrow-up-holidays' fontSize='24px' onClick={() => setShowHolidays(false)}>
                  <Icon name='arrow_drop_up' />
                </Button>
              : <Button data-testid='button-arrow-down-holidays' fontSize='24px' onClick={() => setShowHolidays(true)}>
                  <Icon name='arrow_drop_down' />
                </Button>
              }
            </HoursSection>
          )}
        </div>
  }

  const day = getDay(getNow())
  const normalArray = Array.isArray(normal) ? normal : undefined
  const sortedHours = sortedDaysOfWeek(day === 0 ? 6 : day - 1, normalArray)

  return (
    isLoading ? <Spinner />
    : !sortedHours ? null
    : <HoursSection>
        <div>
          {R.map.indexed(sortedHours, (singleDay, index) => {
            const { dayString, hoursString } = formatDayOfWeekHours(singleDay, holidaysByDate)

            return !showHours && index > 0 ?
                null
              : <SingleDay data-testid={`hours-for-${singleDay.day}`} isBold={index === 0} key={singleDay.day}>
                  <Day>{dayString}</Day>
                  <div>{hoursString}</div>
                </SingleDay>
          })}
        </div>
        {showHours ?
          <Button data-testid='button-arrow-up-hours' fontSize='24px' onClick={() => setShowHours(false)}>
            <Icon name='arrow_drop_up' />
          </Button>
        : <Button data-testid='button-arrow-down-hours' fontSize='24px' onClick={() => setShowHours(true)}>
            <Icon name='arrow_drop_down' />
          </Button>
        }
      </HoursSection>
  )
}

export { SingleDay, sortedDaysOfWeek }
export default DayOfWeekHoursList
